import { useEffect, useState, useCallback } from 'react';
import { API } from '../config/config';
import { useAuth } from './useAuth';
import toast from 'react-hot-toast';

// Define the Address interface with id for existing addresses
export interface Address {
  id: string;
  line1: string;
  line2?: string;
  city?: string;
  state: string;
  postalCode: string;
  country: string;
  phoneNumber: string;
}

// Define the AddressRequest interface for creating new addresses
export interface AddressRequest {
  line1: string;
  line2?: string;
  city?: string;
  state: string;
  postalCode: string;
  country: string;
  phoneNumber: string;
}

// Define the AdminAddressRequest interface for admin operations
export interface AdminAddressRequest {
  email: string;
  address: AddressRequest;
}

export const useAddressList = (buyerEmail?: string) => {
  const { enhancedFetch } = useAuth();
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch addresses for a buyer
  const fetchAddresses = useCallback(async (email?: string) => {
    if (!email && !buyerEmail) {
      setLoading(false);
      return;
    }

    const targetEmail = email || buyerEmail;
    setLoading(true);
    setError(null);

    try {
      const endpoint = `${API.HOST}/api/v1/address/buyer?email=${encodeURIComponent(targetEmail as string)}`;
      const response = await enhancedFetch(endpoint, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });

      if (Array.isArray(response)) {
        setAddresses(response);
      } else {
        console.error('Unexpected response format:', response);
        setAddresses([]);
        setError('Unexpected response format from server');
      }
    } catch (err) {
      console.error('Error fetching addresses:', err);
      setError(err.message || 'Failed to load addresses');
      setAddresses([]);
    } finally {
      setLoading(false);
    }
  }, [buyerEmail, enhancedFetch]);

  // Create a new address
  const createAddress = async (data: AdminAddressRequest): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      await enhancedFetch(`${API.HOST}/api/v1/address/buyer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      // Refresh the address list
      await fetchAddresses(data.email);
      toast.success('Address added successfully');
      return true;
    } catch (err) {
      console.error('Error creating address:', err);
      setError(err.message || 'Failed to create address');
      toast.error('Failed to add address');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Delete an address
  const deleteAddress = async (addressId: string): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      await enhancedFetch(`${API.HOST}/api/v1/address/${addressId}`, {
        method: 'DELETE'
      });

      // Update the addresses list locally
      setAddresses(prevAddresses => prevAddresses.filter(address => address.id !== addressId));
      toast.success('Address deleted successfully');
      return true;
    } catch (err) {
      console.error('Error deleting address:', err);
      setError(err.message || 'Failed to delete address');
      toast.error('Failed to delete address');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch on component mount or when buyerEmail changes
  useEffect(() => {
    if (buyerEmail) {
      fetchAddresses();
    }
  }, [buyerEmail, fetchAddresses]);

  return {
    addresses,
    loading,
    error,
    fetchAddresses,
    createAddress,
    deleteAddress
  };
};